import type { RunStarted } from '@logspot-platform/platform-types';
import { ActionRunsService } from './action-runs-service';
import { ApiError, api } from './api-client';

type SuccessEnvelope<T> = { data: T };

// On-demand invocations of the form-driven AI actions (Prospect Research, Lead
// Qualification, Outreach Draft) — the ones that take per-run input instead of
// running from a saved config. Mirrors the backend admin endpoints
// (api/admin/action-invocations.ts). Org owner/admin, session auth.

// ── Shared inputs ────────────────────────────────────────────────────────────
export type CompanyInput = {
  name?: string;
  website?: string;
  linkedin?: string;
  industry?: string;
  employeeCount?: string | number;
  hqLocation?: string;
};

export type ContactInput = {
  firstName?: string;
  lastName?: string;
  jobTitle?: string;
  linkedin?: string;
};

export type LeadInput = {
  firstName?: string;
  lastName?: string;
  jobTitle?: string;
  primaryEmail?: string;
  linkedin?: string;
};

// ── Prospect Research (prospect.research) ────────────────────────────────────
export type ProspectResearchRequest = {
  company: CompanyInput;
  contact: ContactInput;
  /** Saves the finished brief to this Target Account. */
  profile_id?: string;
};

export type ProspectResearchResult = {
  brief: ProspectBrief;
  /** Set when the server saved the brief to the requested Target Account. */
  savedProfileId: string | null;
};

export type ProspectSignal = { summary: string; date?: string; url: string };

export type ProspectPrioritization = 'lean_in' | 'wait' | 'pass';

// An objection to anticipate, paired with a suggested response (concord-gtm
// sales-prospect port). Stored payloads may be snake_case; the brief renderer
// tolerates both, so these optional fields are read defensively.
export type ProspectObjection = { objection: string; response: string };

export type ProspectBrief = {
  company: {
    products: string[];
    positioning: string;
    recentSignals: ProspectSignal[];
    competitors: string[];
  };
  contact: {
    responsibilities: string;
    priorRoles: string[];
    personalizationHooks: string[];
  };
  prioritization: ProspectPrioritization;
  // Optional 4-lens upgrade fields (PRD 5.3); backward-compatible — older
  // briefs omit them and the renderer skips absent sections.
  whyNow?: string;
  openingAngle?: string;
  objections?: ProspectObjection[];
  modelId: string;
};

// ── Lead Qualification (lead.qualify) ────────────────────────────────────────
export type LeadQualifyRequest = {
  lead: LeadInput;
  company: CompanyInput;
  crm_status?: string;
  // Only a queued run reads it: the server saves the fit onto this account when the run ends.
  fit_profile_id?: string;
};

export type QualifyVerdict = 'qualified' | 'needs_review' | 'disqualified';

export type QualifyResult = {
  fitScore: number;
  verdict: QualifyVerdict;
  verdictReasons: string[];
  disqualificationFlags: string[];
  // 1–2 discovery questions per gap (concord-gtm sales-qualify port, PRD 5.4).
  // Optional & backward-compatible — older results omit it.
  discoveryQuestions?: string[];
  // Org personas (exact names) this lead matches — fills the account page's
  // Matched Personas rail on newer runs.
  matchedPersonas?: string[];
  crmStatus: string;
  modelId: string;
};

// ── Outreach Draft (outreach.draft) ──────────────────────────────────────────
export type OutreachChannel = 'email' | 'linkedin' | 'both';

export type OutreachDraftRequest = {
  lead: LeadInput;
  company: CompanyInput;
  channel?: OutreachChannel;
  offer?: string;
  prospect_brief?: string;
};

export type OutreachStep = {
  day: number;
  channel: 'email' | 'linkedin';
  subject?: string;
  body: string;
  ask: string;
  sourceHook?: string;
};

export type OutreachResult = { steps: OutreachStep[]; modelId: string };

// ── Per-run cost estimate (credits-only) ─────────────────────────────────────
// GET /admin/actions/:key/estimate → the customer-facing estimate for one run.
// `data_credits` is always the {expected, max} estimate form; fixed lookups
// return expected === max. Credits only — the payload never carries $/USD.
export type ActionEstimate = {
  actions: number;
  data_credits: { expected: number; max: number };
};

// Web research with page reads can run well past a normal action.
const RESEARCH_MAX_WAIT_MS = 15 * 60_000;

export const ActionInvocationsService = {
  // POST /api/admin/actions/prospect-research starts a background run (research
  // outlasts the 60s load balancer limit); this waits for it.
  runProspectResearch: async (
    input: ProspectResearchRequest,
  ): Promise<ProspectResearchResult> => {
    const r = await api.post<SuccessEnvelope<{ run_id: string }>>(
      '/admin/actions/prospect-research',
      input,
    );
    const run = await ActionRunsService.waitFor(
      r.data.run_id,
      'Research is still running. The brief will appear in Activity when it finishes.',
      RESEARCH_MAX_WAIT_MS,
    );
    if (
      run.status !== 'succeeded' ||
      !run.result ||
      typeof run.result !== 'object'
    ) {
      throw new ApiError(
        500,
        run.error ?? 'The research run failed. Try again.',
      );
    }
    const { profile_id: savedProfileId, ...brief } = run.result as Record<
      string,
      unknown
    >;
    return {
      brief: brief as unknown as ProspectBrief,
      savedProfileId:
        typeof savedProfileId === 'string' ? savedProfileId : null,
    };
  },

  // POST /api/admin/actions/lead-qualify
  runLeadQualify: async (
    input: LeadQualifyRequest,
  ): Promise<QualifyResult | RunStarted> => {
    const r = await api.post<SuccessEnvelope<QualifyResult | RunStarted>>(
      '/admin/actions/lead-qualify',
      input,
    );
    return r.data;
  },

  // POST /api/admin/actions/outreach-draft
  runOutreachDraft: async (
    input: OutreachDraftRequest,
  ): Promise<OutreachResult | RunStarted> => {
    const r = await api.post<SuccessEnvelope<OutreachResult | RunStarted>>(
      '/admin/actions/outreach-draft',
      input,
    );
    return r.data;
  },

  // GET /admin/actions/:key/estimate — live per-run cost estimate (credits
  // only). Optional `units` scales per-unit estimates (e.g. account.watch's
  // per-account weight × watched count).
  estimate: async (key: string, units?: number): Promise<ActionEstimate> => {
    const query = units != null ? `?units=${encodeURIComponent(units)}` : '';
    const r = await api.get<SuccessEnvelope<ActionEstimate>>(
      `/admin/actions/${encodeURIComponent(key)}/estimate${query}`,
    );
    return r.data;
  },
};
